import { attributes, dia, shapes, ui, util, Vectorizer } from "@clientio/rappid";
import { extend, clone } from "lodash-es";
import { ElementController } from "./base/element-controller";
import { PreferenceStore } from "../services/preference-store";
import { getSelectionAttrs } from "../rappid/textedit-helper";
import RappidContext from "../rappid/rappid-context";

const Position = ui.Halo.HandlePosition;

const DEFAULT_NOTE_COLOR = "#FFF9B2";
const DEFAULT_BORDER_COLOR = "#000000";
const DEFAULT_BORDER_WIDTH = 0;
const DEFAULT_TEXT_COLOR = "#333333";

const MIN_WIDTH = 60;
const MIN_HEIGHT = 60;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const DEFAULT_ANNOTATIONS: any = {
  attrs: {
    fill: DEFAULT_TEXT_COLOR,
    "font-size": 12,
    "font-weight": "normal",
    "text-decoration": "none",
    "font-style": "normal",
    "font-family": PreferenceStore.INSTANCE.theme.model.text.fontFamily,
  },
};

export type STYLE_KEYS =
  | "fill"
  | "font-size"
  | "font-weight"
  | "text-decoration"
  | "text-align"
  | "font-style"
  | "font-family";

export class Note extends shapes.standard.Rectangle {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  defaults(): any {
    return {
      ...super.defaults,
      type: "Note",
      size: { width: 60, height: 60 },
      linkable: false,
      attrs: {
        root: {
          magnet: false,
        },
        body: {
          refWidth: "100%",
          refHeight: "100%",
          strokeWidth: DEFAULT_BORDER_WIDTH,
          stroke: DEFAULT_BORDER_COLOR,
          fill: DEFAULT_NOTE_COLOR,
          filter: {
            name: "dropShadow",
            args: {
              dx: 0,
              dy: 6,
              blur: 3,
              opacity: 0.2,
            },
          },
        },
        label: extend(
          {
            text: "Note",
            lineHeight: "auto",
            refX: "5px",
            refY: "5px",
            textVerticalAnchor: "top",
            textAnchor: "start",
            annotations: [{ start: 0, end: 4, attrs: DEFAULT_ANNOTATIONS.attrs }],
            fontSize: null,
            displayEmpty: true,
          },
          DEFAULT_ANNOTATIONS.attrs
        ),
      },
    };
  }

  get text(): string {
    return this.attr(["label", "text"]);
  }

  resetDefaults() {
    this.removeAttr(["label", "annotations"]);
    this.attr(["label", "annotations"], [DEFAULT_ANNOTATIONS]);
    this.setFillColor(DEFAULT_NOTE_COLOR);
  }

  getFillColor(): string {
    return this.attr(["body", "fill"]);
  }

  setFillColor(color: string) {
    this.attr(["body", "fill"], color);
  }

  getTextColor(): string {
    return this.getStyle("fill", "#333333") as string;
  }

  setText(text: string): void {
    this.attr("label/text", text);
  }

  setTextColor(color: string) {
    this.setStyle("fill", color);
  }

  getTextBold(): boolean {
    return this.getStyle("font-weight", "normal") === "bold";
  }

  setTextBold(enabled: boolean) {
    this.setStyle("font-weight", enabled ? "bold" : "normal");
  }

  getTextItalic(): boolean {
    return this.getStyle("font-style", "normal") === "italic";
  }

  setTextItalic(enabled: boolean) {
    this.setStyle("font-style", enabled ? "italic" : "normal");
  }

  getTextUnderline(): boolean {
    return this.getStyle("text-decoration", "none") === "underline";
  }

  setTextUnderline(enabled: boolean) {
    this.setStyle("text-decoration", enabled ? "underline" : "none");
  }

  getTextAlign(): number {
    const align = this.getStyle("text-align", "left") as string;
    if (align === "left") return 0;
    if (align === "center") return 1;
    if (align === "right") return 2;
    return 0;
  }
  setTextAlign(value: number) {
    if (value === 0) this.setStyle("text-align", "left");
    else if (value === 1) this.setStyle("text-align", "center");
    else if (value === 2) this.setStyle("text-align", "right");
  }

  get combinedAnnotationsAttrs(): attributes.SVGAttributes {
    return getSelectionAttrs({ start: 0, end: this.text.length }, this.annotations);
  }

  get annotations() {
    return this.attr(["label", "annotations"]);
  }
  set annotations(annots: Array<Vectorizer.TextAnnotation>) {
    this.removeAttr(["label", "annotations"]);
    this.attr(["label", "annotations"], annots);
  }

  getStyle(key: STYLE_KEYS, defaultValue?: string | number): string | number | undefined {
    const selectionLength = ui.TextEditor.getSelectionLength();
    let selectionAttrs = ui.TextEditor.getSelectionAttrs(
      selectionLength === 0 ? ui.TextEditor.findAnnotationsUnderCursor() : ui.TextEditor.findAnnotationsInSelection()
    );
    if (selectionAttrs === null) selectionAttrs = this.combinedAnnotationsAttrs ?? DEFAULT_ANNOTATIONS.attrs;
    return selectionAttrs[key] ?? defaultValue;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setStyle(key: string, value: any) {
    const selectionAttrs = ui.TextEditor.getSelectionAttrs(ui.TextEditor.findAnnotationsInSelection());
    const attrs = selectionAttrs
      ? clone(selectionAttrs)
      : this.annotations.length === 1
        ? clone(this.annotations[0].attrs)
        : clone(DEFAULT_ANNOTATIONS.attrs);
    attrs[key] = value;
    const annotations = ui.TextEditor.getAnnotations();
    const selectionLength = ui.TextEditor.getSelectionLength();
    const range = ui.TextEditor.getSelectionRange();

    if (selectionLength === 0) {
      // cursor inside text, but no selection
      ui.TextEditor.setCurrentAnnotation(attrs);
    } else if (selectionLength > 0) {
      // multiple chars selected
      const newAnnotation = {
        start: range.start,
        end: range.end,
        attrs: attrs,
      };
      ui.TextEditor.applyAnnotations(annotations.concat(newAnnotation));
    } else {
      // nothing selected, text editor not active => change default annotation
      const newAnnotation = {
        start: 0,
        end: this.text.length,
        attrs: attrs,
      };
      this.annotations = [newAnnotation];
    }
  }

  getFontSize(): number {
    return this.getStyle("font-size", 10) as number;
  }

  setFontSize(size: number) {
    this.setStyle("font-size", size);
  }

  getMinSize(view: NoteView): { width: number; height: number } {
    const textBBox = view.vel.findOne("text").getBBox().inflate(5);
    return { width: Math.max(MIN_WIDTH, textBBox.width), height: Math.max(MIN_HEIGHT, textBBox.height) };
  }
}

export class NoteView extends dia.ElementView {
  autoSize(shrink?: boolean) {
    const textBBox = this.vel.findOne("text").getBBox().inflate(5);
    const noteBBox = this.vel.findOne("rect").getBBox();
    this.model.resize(
      shrink ? Math.max(MIN_WIDTH, textBBox.width) : Math.max(textBBox.width, noteBBox.width),
      shrink ? Math.max(MIN_HEIGHT, textBBox.height) : Math.max(textBBox.height, noteBBox.height)
    );
  }
}

export class NoteController extends ElementController {
  constructor(view: dia.CellView, context: RappidContext) {
    super(view, context);
    this.view.model.on("change:attrs", this.onAttributeChange.bind(this));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private onAttributeChange(model: Note, attrs: any, options: any) {
    // Expand the note if the label/annotations changed
    if (options.propertyPath && options.propertyPath.includes("label/annotations")) {
      (this.view as NoteView).autoSize(false);
    }
  }

  createFreetransform() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const _this = this;
    const view = this.view as NoteView;
    const model = this.view.model as Note;
    const ft = new (class extends ui.FreeTransform {
      constructor() {
        super({
          theme: "modern",
          cellView: view,
          allowRotation: false,
          resizeGrid: { width: 1, height: 1 },
          preserveAspectRatio: view.model.attributes["fixedRatio"],
          minWidth(cell, FreeTransform, direction) {
            const { width } = model.getMinSize(view);
            return width;
          },
          minHeight(cell, FreeTransform, direction) {
            const { height } = model.getMinSize(view);
            return height;
          },
          padding: 0,
        });
      }
    })();
    ft.render();
  }

  onCellPointerDblClick(evt?: joint.dia.Event) {
    this.context.nameTextEditor.create(this.view, "label", ["Escape"]);
    this.context.nameTextEditor.on("text:change", () => (this.view as NoteView).autoSize());
  }

  createHalo() {
    const halo = super.createHalo();
    halo.on("action:preferred:pointerdown", (evt) => {
      evt.stopPropagation();
      (this.view as NoteView).autoSize(true);
    });
    return halo;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChangeSize(element: dia.Element<dia.Element.Attributes, dia.ModelSetOptions>, newSize: any, opt: any): void {}

  getHaloConfig() {
    return [
      {
        name: "remove",
        position: Position.NW,
        events: { pointerdown: "removeElement" },
        attrs: {
          ".handle": {
            "data-tooltip-class-name": "small",
            "data-tooltip": "Remove the object",
            "data-tooltip-position": "right",
            "data-tooltip-padding": 25,
          },
        },
      },
      {
        name: "preferred",
        position: Position.SW,
        events: { pointerdown: "preferred" },
        attrs: {
          ".handle": {
            "data-tooltip-class-name": "small",
            "data-tooltip": "Resize to fit contents",
            "data-tooltip-position": "right",
            "data-tooltip-padding": 15,
          },
        },
      },
    ];
  }
}

util.assign(shapes, {
  Note,
  NoteView,
  NoteController,
});
